import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import apiClient from '@/shared/api/client';

interface Workflow {
  id: string;
  locationId: string;
  type: string;
  status: 'running' | 'completed' | 'failed';
  resultIds: string[];
  error: string | null;
  startedAt: string;
  completedAt: string | null;
  timeoutAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export function useWorkflows(locationId: string, type?: string) {
  return useQuery({
    queryKey: ['workflows', locationId, type],
    queryFn: () =>
      apiClient
        .get<ApiResponse<Workflow[]>>('/workflows', {
          params: { locationId, ...(type ? { type } : {}) },
        })
        .then((r) => r.data.data),
    enabled: !!locationId,
    // Always current on mount / tab focus (the app's 5-minute staleTime would
    // otherwise serve an old list), and an idle page still checks now and
    // then: a run started elsewhere — another tab, a teammate, a follow-up
    // job — used to go unseen until a reload.
    staleTime: 0,
    refetchInterval: (query) => {
      const workflows = query.state.data;
      const hasRunning = workflows?.some((w) => w.status === 'running');
      return hasRunning ? 5000 : 30_000;
    },
    refetchIntervalInBackground: false,
  });
}

/**
 * Returns the latest workflow for a given type, plus helpers.
 * When a running workflow settles, `invalidateKeys` are invalidated (on
 * failure too, so the page shows whatever was kept) and the optional
 * `onCompleted` / `onFailed` callbacks fire once.
 */
export function useWorkflowStatus(
  locationId: string,
  type: string,
  invalidateKeys: QueryKey[],
  callbacks: { onCompleted?: () => void; onFailed?: (error: string | null) => void } = {},
) {
  const queryClient = useQueryClient();
  const { data: workflows, isLoading } = useWorkflows(locationId, type);

  const latest = workflows?.[0] ?? null;
  const isRunning = latest?.status === 'running';
  const isFailed = latest?.status === 'failed';
  const error = latest?.error ?? null;

  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  // Track previous status to detect running → completed / failed transitions
  const prevStatusRef = useRef(latest?.status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = latest?.status;
    if (prev !== 'running') return;

    if (latest?.status === 'completed' || latest?.status === 'failed') {
      for (const key of invalidateKeys) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    }
    if (latest?.status === 'completed') callbacksRef.current.onCompleted?.();
    if (latest?.status === 'failed') callbacksRef.current.onFailed?.(latest.error);
  }, [latest?.status, latest?.error, invalidateKeys, queryClient]);

  return { latest, isRunning, isLoading, isFailed, error };
}

/** Query key prefix for a location's workflows — refetch it right after queueing a job. */
export const workflowsKey = (locationId: string, type?: string) =>
  type ? (['workflows', locationId, type] as const) : (['workflows', locationId] as const);
